import { inject, Injectable } from '@angular/core';
import { FlightType, Pax, PaxInfoModal, PaxQuantityState, PaxType, SearcherState } from '@vueling-ng/vy-cdk-searcher';
import { ModalConfig } from '@vueling-ng/vy-ds/src/lib/components/globals/modal';
import { GenericModalComponent } from 'app/shared/generic-modal/generic-modal.component';
import { DeeplinkParamsBuilder } from 'app/shared/services/deeplink-params.builder';
import { SmartDeeplinkValues } from './enums/smart-deep-link.values';

@Injectable({
  providedIn: 'root'
})
export class SearcherWrapperService {
  private readonly _deeplinkParamsBuilder = inject(DeeplinkParamsBuilder);

  public calculateTotalPaxAfterSubmit(pax: PaxQuantityState): number {
    return pax.adultsQuantity + pax.childrenQuantity + pax.extraSeatsQuantity + pax.infantsQuantity;
  }

  public generateSmartDeeplink(searcherState: SearcherState, culture: string, currency: string): string {
    const params = new Map<string, string>([
      [SmartDeeplinkValues.Origin, searcherState.selectedOrigin.code],
      [SmartDeeplinkValues.Destination, searcherState.selectedDestination.code],
      [SmartDeeplinkValues.OutboundDate, this.parseDate(searcherState.outboundDate!)],
      [SmartDeeplinkValues.AdultsAmount, `${searcherState.pax.adultsQuantity}`],
      [SmartDeeplinkValues.ChildrenAmount, `${searcherState.pax.childrenQuantity}`],
      [SmartDeeplinkValues.InfantsAmount, `${searcherState.pax.infantsQuantity}`],
      [SmartDeeplinkValues.Culture, culture],
      [SmartDeeplinkValues.Currency, currency]
    ]);
    if (searcherState.flightType === FlightType.RoundTrip) {
      params.set('rd', this.parseDate(searcherState.returnDate!));
    }
    return this._deeplinkParamsBuilder.generateDeeplinkParams(params);
  }

  public countTotalPax(pax: Pax[]): number {
    return pax.reduce((acc, cur) => acc + cur.value, 0);
  }

  public shouldOpenUnacompaniedMinorsModal(pax: Pax[]): boolean {
    const adults = this.getPaxAmountPerType(pax, PaxType.Adults);
    const minors = this.getPaxAmountPerType(pax, PaxType.Children);
    return adults === 0 && minors > 0;
  }

  public generateModalConfig(modalData: PaxInfoModal): ModalConfig {
    return {
      title: modalData.title,
      content: GenericModalComponent,
      primaryAction: {
        label: modalData.closeButton,
        closeModal: true
      },
      customData: { label: modalData.content, styles: 'bodyM' },
      undismissable: true
    } as ModalConfig;
  }

  private getPaxAmountPerType(totalPax: Pax[], paxType: PaxType): number {
    return totalPax.filter((state) => state.paxType === paxType)[0]?.value ?? 0;
  }

  private parseDate(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }
}
